import { useState, useRef, useEffect } from "react";
import { X, Send, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GHOST_LOGO = "https://customer-assets.emergentagent.com/job_slugs-124/artifacts/ux47ad2h_image.png";

const quickReplies = [
  { label: "Plans & Pricing" },
  { label: "How to Sign Up" },
  { label: "Port My Number" },
  { label: "SIM & Activation" },
  { label: "Billing & Payment" },
  { label: "Contact Support" },
];

const welcomeMessage = {
  role: "assistant",
  text: "Hey! I'm Ghost, your Prestigious Wireless assistant. How can I help you today?",
};

function getSessionId() {
  let sid = sessionStorage.getItem("ghost_session");
  if (!sid) {
    sid = "ghost-" + Math.random().toString(36).slice(2, 12);
    sessionStorage.setItem("ghost_session", sid);
  }
  return sid;
}

export default function GhostWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([welcomeMessage]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const sendToGhost = async (text) => {
    setMessages(prev => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ghost/chat`, {
        message: text,
        session_id: sessionId.current,
      });
      setMessages(prev => [...prev, { role: "assistant", text: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, I'm having trouble connecting. Please try again or email support@prestigius-wireless.org." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickReply = (qr) => sendToGhost(qr.label);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    sendToGhost(msg);
  };

  const showQuickReplies = messages.length <= 2 && !loading;

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 w-[340px] sm:w-[380px] max-h-[520px] bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl shadow-2xl shadow-black/60 z-[9999] flex flex-col overflow-hidden" data-testid="ghost-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2A] bg-[#0A0A0A]">
            <div className="flex items-center gap-3">
              <img src={GHOST_LOGO} alt="Ghost" className="w-9 h-9 rounded-full object-cover border border-[#2A2A2A]" />
              <div>
                <p className="text-[#F5F5F5] font-outfit font-semibold text-sm leading-tight">Ghost</p>
                <p className="text-[#A0A0A0] text-[10px] font-manrope">Prestigious Wireless Assistant</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors" data-testid="ghost-close-button">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[260px] max-h-[340px]">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm font-manrope leading-relaxed whitespace-pre-line ${
                  msg.role === "user"
                    ? "bg-[#D4AF37] text-black rounded-br-md"
                    : "bg-[#1A1A1A] text-[#F5F5F5] rounded-bl-md"
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1A] text-[#A0A0A0] px-4 py-3 rounded-2xl rounded-bl-md text-sm font-manrope flex items-center gap-2">
                  <span className="flex gap-1"><span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay:"0ms"}} /><span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay:"150ms"}} /><span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{animationDelay:"300ms"}} /></span>
                </div>
              </div>
            )}
            {showQuickReplies && (
              <div className="flex flex-wrap gap-2 pt-2">
                {quickReplies.map((qr, i) => (
                  <button key={i} onClick={() => handleQuickReply(qr)}
                    className="text-xs font-manrope px-3 py-1.5 rounded-full border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                    data-testid={`ghost-quick-reply-${i}`}>
                    {qr.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-[#2A2A2A] px-3 py-3 bg-[#0A0A0A]">
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask Ghost anything..."
                disabled={loading}
                className="flex-1 bg-[#1A1A1A] border border-[#2A2A2A] focus:border-[#D4AF37] rounded-full px-4 py-2 text-sm text-[#F5F5F5] font-manrope outline-none transition-all disabled:opacity-50"
                data-testid="ghost-chat-input"
              />
              <button onClick={handleSend} disabled={loading}
                className="w-9 h-9 rounded-full bg-[#D4AF37] hover:bg-[#F0C541] flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-50"
                data-testid="ghost-send-button">
                <Send className="w-4 h-4 text-black" />
              </button>
            </div>
            <p className="text-[#A0A0A0]/50 text-[9px] font-manrope text-center mt-2">
              Ghost powered by Prestigious Companies
            </p>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 sm:right-6 z-[9999] w-14 h-14 rounded-full shadow-xl shadow-black/50 overflow-hidden border-2 border-[#D4AF37] hover:scale-110 transition-transform duration-300"
        data-testid="ghost-widget-button"
      >
        {open ? (
          <div className="w-full h-full bg-[#0F0F0F] flex items-center justify-center"><X className="w-6 h-6 text-[#D4AF37]" /></div>
        ) : (
          <img src={GHOST_LOGO} alt="Ghost AI" className="w-full h-full object-cover" />
        )}
      </button>
    </>
  );
}
